/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import {
    CashBoxesListDto,
    CashBoxDetailsDto,
    CashBoxStatusesHistoryDto,
    CashBoxTimeDeltaHistoryDto,
    CashBoxStateHistoryDto,
    CashBoxReplacementHistoryDto,
    CashBoxAlertsHistoryDto,
    CashBoxesMap,
    CashBoxPoint,
    CashBoxDetailsForMap,
    CashboxLocationData,
    HistoryLocationsList,
    GpsTrackingLocationsList,
    CheckSumsHistoryDto,
    CashBoxAuditHistoryDto,
    CashBoxLastDocument,
    CashBoxErrorsHistoryDto,
} from './cashBoxesClient.typings';
import { delayedResponse } from '../companies/companiesMock';
import { makeMockResponse } from '../mockUtils';
import {
    cashBoxDetailsFactory,
    cashBoxListItemFactory,
    CashBoxStatusFactory,
    CashBoxTimeDeltaFactory,
    CashBoxStateFactory,
    CashBoxAuditHistoryItemFactory,
    CashBoxReplacementFactory,
    CashBoxAlertsFactory,
    TurnoversByPeriodFactory,
    cashBoxOnMapFactory,
    cashBoxDetailsForMapFactory,
    cashBoxLocationDataFactory,
    locationsListItemFactory,
    checksumsHistoryItemFactory,
    aiClassificationsHistoryItemFactory,
    cashBoxLastDocumentFactory,
    errorsHistoryItemFactory,
} from './cashBoxesFactories';
import { AiClassificationsHistoryDto, RegistryTurnoversByPeriodDto } from '../apiCommon.typings';

const CASH_BOXES_URL: RegExp = /\/1.0.0\/cashboxes$/;
const CASH_BOX_DETAILS_URL: RegExp = /\/cashboxes\/\d+$/;
const CASH_BOXES_FOR_MAP_URL: RegExp = /\/map\/cashBoxes/;
const CASH_BOX_DETAILS_FOR_MAP_URL: RegExp = /map\/cashBoxes\/\d+$/;
const CHANGE_CASH_BOX_STATUS: RegExp = /\/cashboxes\/\d+\/status/;
const BLOCK_CASH_BOX_URL: RegExp = /\/cashboxes\/\d+\/block$/;
const UNBLOCK_CASH_BOX_URL: RegExp = /\/cashboxes\/\d+\/unblock$/;
const REMOVE_BLOCK_UNBLOCK_SCHEDULE_URL: RegExp = /\/cashboxes\/\d+\/blocks$/;
const CASH_BOXES_GET_TURNOVERS: RegExp = /\/cashboxes\/\d+\/turnovers/;
const CASH_BOX_LAST_DOCUMENT_URL: RegExp = /\/cashboxes\/\d+\/lastDocument/;
const CASH_BOXES_STATUSES_HISTORY = /\/cashboxes\/\d+\/statusesHistory/;
const CASH_BOXES_TIME_DELTA_HISTORY = /\/cashboxes\/\d+\/timeDeltaHistory/;
const CASH_BOXES_STATE_HISTORY = /\/cashboxes\/\d+\/statesHistory/;
const CASH_BOXES_AUDIT_HISTORY = /\/cashboxes\/\d+\/auditHistory/;
const CASH_BOXES_REPLACEMENT_HISTORY = /\/cashboxes\/\d+\/replacements/;
const CASH_BOXES_ALERTS_HISTORY = /\/alerts\/cashboxes\/\d+/;
const CASHBOX_LOCATION_DATA = /\/cashboxes\/\d+\/gps$/;
const CASHBOX_HISTORY_LOCATIONS = /\/cashboxes\/\d+\/gpsHistory$/;
const CASHBOX_GPS_TRACKING_LOCATIONS = /\/cashboxes\/\d+\/gpsTracking$/;
const CHECKSUMS_HISTORY = /\/cashboxes\/\d+\/checksums$/;
const AI_CLASSIFICATIONS_HISTORY = /\/cashboxes\/\d+\/classificationsHistory$/;
const CHANGE_CASHBOX_INTEGRATION_STATUS = /\/cashboxes\/integrations$/;
const CASH_BOXES_ERRORS_HISTORY = /\/cashboxes\/\d+\/errors/;

export function addCashBoxesMockApi(mockAdapter: MockAdapter): void {
    const cashBoxesBackEnd = new CashBoxForMapBackendStore({ total: 10000 });

    mockAdapter.onPost(CASH_BOXES_FOR_MAP_URL).reply((config) => {
        const cashBoxesPoints = cashBoxesBackEnd.getCashBoxes();
        const params = JSON.parse(config.data);

        let data: CashBoxesMap = {
            clusters: cashBoxesClusters,
            points: cashBoxesPoints,
        };

        if (params.hasOwnProperty('taxNumber') && data.points.find((store) => store.taxNumber === params.taxNumber)) {
            data = {
                clusters: [],
                points: [data.points.find((store) => store.taxNumber === params.taxNumber)],
            };
        }

        if (
            params.hasOwnProperty('taxNumber') &&
            data.points.find((store) => store.taxNumber === params.taxNumber) === undefined
        ) {
            data = {
                clusters: [],
                points: [cashBoxesBackEnd.getCashBoxes()[0]],
            };
        }

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASH_BOX_DETAILS_FOR_MAP_URL).reply((config) => {
        const data: CashBoxDetailsForMap = cashBoxDetailsForMapFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOX_LAST_DOCUMENT_URL).reply((config) => {
        const data: CashBoxLastDocument = cashBoxLastDocumentFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_AUDIT_HISTORY).reply((config) => {
        const data: CashBoxAuditHistoryDto = {
            count: 30,
            audits: CashBoxAuditHistoryItemFactory.list({ count: config.params.limit }),
        };
        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_REPLACEMENT_HISTORY).reply((config) => {
        const data: CashBoxReplacementHistoryDto = {
            parents: CashBoxReplacementFactory.list({ count: 1 }),
            children: CashBoxReplacementFactory.list({ count: 3 }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_ALERTS_HISTORY).reply((config) => {
        const data: CashBoxAlertsHistoryDto = {
            count: 25,
            alerts: CashBoxAlertsFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_STATUSES_HISTORY).reply((config) => {
        const data: CashBoxStatusesHistoryDto = {
            count: 30,
            statuses: CashBoxStatusFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_TIME_DELTA_HISTORY).reply((config) => {
        const data: CashBoxTimeDeltaHistoryDto = {
            count: 40,
            timeDeltas: CashBoxTimeDeltaFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_STATE_HISTORY).reply((config) => {
        const data: CashBoxStateHistoryDto = {
            count: 50,
            states: CashBoxStateFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_GET_TURNOVERS).reply((config) => {
        const data: RegistryTurnoversByPeriodDto[] = TurnoversByPeriodFactory.list({ count: 12 });
        TurnoversByPeriodFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1500);
    });

    mockAdapter.onPost(CASH_BOXES_URL).reply((config) => {
        const body = JSON.parse(config.data);
        const data: CashBoxesListDto = {
            afterKeyTaxNumber: '234318932',
            cashBoxes: cashBoxListItemFactory.list({ count: body.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOX_DETAILS_URL).reply((config) => {
        const data: CashBoxDetailsDto = cashBoxDetailsFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onPost(CHANGE_CASH_BOX_STATUS).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), 300);
    });

    mockAdapter.onPost(BLOCK_CASH_BOX_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });

    mockAdapter.onPost(UNBLOCK_CASH_BOX_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });

    mockAdapter.onDelete(REMOVE_BLOCK_UNBLOCK_SCHEDULE_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASHBOX_LOCATION_DATA).reply((config) => {
        const data: CashboxLocationData = cashBoxLocationDataFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASHBOX_HISTORY_LOCATIONS).reply((config) => {
        const data: HistoryLocationsList = {
            count: 30,
            items: locationsListItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASHBOX_GPS_TRACKING_LOCATIONS).reply((config) => {
        const data: GpsTrackingLocationsList = {
            accuracy: 1000,
            items: locationsListItemFactory.list({ count: 50 }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CHECKSUMS_HISTORY).reply((config) => {
        const data: CheckSumsHistoryDto = {
            count: config.params.limit * 3,
            items: checksumsHistoryItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(AI_CLASSIFICATIONS_HISTORY).reply((config) => {
        const data: AiClassificationsHistoryDto = {
            count: config.params.limit * 3,
            items: aiClassificationsHistoryItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onGet(CASH_BOXES_ERRORS_HISTORY).reply((config) => {
        const data: CashBoxErrorsHistoryDto = {
            count: config.params.limit * 3,
            items: errorsHistoryItemFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onPost(CHANGE_CASHBOX_INTEGRATION_STATUS).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });
}

class CashBoxForMapBackendStore {
    private cashBoxes: CashBoxPoint[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            const southWest = { lat: 40.384212768155045, lon: 49.83140945434571 };
            const northEast = { lat: 40.43427422075976, lon: 49.902734756469734 };

            const lonSpan = northEast.lon - southWest.lon;
            const latSpan = northEast.lat - southWest.lat;

            this.createCashBox(southWest.lat + latSpan * Math.random(), southWest.lon + lonSpan * Math.random());
        }
    }

    public getCashBoxes() {
        return this.cashBoxes;
    }

    private createCashBox(lat: number, lon: number): void {
        const cashBox = cashBoxOnMapFactory.item();

        cashBox.lat = lat;
        cashBox.lon = lon;

        this.cashBoxes.push(cashBox);
    }
}

const cashBoxesClusters = [
    {
        count: 2000,
        geoHash: 'tp5myn',
    },
    {
        count: 1400,
        geoHash: 'tp5myx',
    },
    {
        count: 1200,
        geoHash: 'tp5myg',
    },
    {
        count: 999,
        geoHash: 'tp6myx',
    },
    {
        count: 1500,
        geoHash: 'tp9myx',
    },
];
